import {
    ChangeDetectionStrategy,
    Component,
    input,
    InputSignal,
    OnInit,
    signal,
    ViewEncapsulation,
    WritableSignal,
} from "@angular/core";
import { TypeManagerDecorator } from "../main/type.map.service";
import { S25LoadingInlineModule } from "../modules/s25-loading/s25.loading.inline.module";
import { Nullable } from "../pojo/Util";

@TypeManagerDecorator("s25-ng-usage")
@Component({
    selector: "s25-ng-usage",
    template: `
        @if (isLoading()) {
            <s25-ng-loading-inline-static />
        }
        @if (usage()) {
            @if (usage().error) {
                <div>{{ usage().error }}</div>
            } @else {
                <div class="types">
                    <h3 class="c-margin-bottom--half">{{ usage().name }}</h3>
                    @for (type of usage().types ?? []; track type.type) {
                        @if (!!type.uses.length) {
                            <div class="list-container c-margin-bottom--single" tabindex="0">
                                <div class="usage-header c-margin-bottom--quarter">
                                    <b>{{ type.type }}</b>
                                </div>
                                <ul>
                                    @for (use of type.uses; track use.name) {
                                        <li>
                                            <span>{{ use.name }}</span>
                                        </li>
                                    }
                                </ul>
                            </div>
                        }
                        @if (!type.uses?.length && type.noUsesMessage) {
                            <div>{{ type.noUsesMessage }}</div>
                        }
                    }
                </div>
            }
        }
    `,
    styles: `
        .types {
            padding: 1em;
            background-color: #fafafa;
        }

        .list-container {
            border: 1px solid slategray;
            border-radius: 10px;
            padding: 10px;
            background-color: #fff;
            max-height: 40vh;
            overflow-y: auto;
        }

        .usage-header {
            position: sticky;
            top: -10px;
            background-color: #fff;
        }

        ::ng-deep .popover-body:has(s25-ng-usage) {
            padding: 0;
        }

        ::ng-deep body.nm-party--on s25-ng-usage .types {
            background-color: #3c3d46;
        }

        ::ng-deep body.nm-party--on s25-ng-usage .list-container,
        ::ng-deep body.nm-party--on s25-ng-usage .usage-header {
            background-color: #313036;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: true,
    imports: [S25LoadingInlineModule],
})
export class S25UsageComponent implements OnInit {
    service: InputSignal<() => Promise<Usage>> = input.required();

    isLoading: WritableSignal<boolean> = signal<boolean>(false);
    usage: WritableSignal<Nullable<Usage>> = signal(undefined);

    async ngOnInit() {
        this.isLoading.set(true);
        this.usage.set(await this.service()());
        this.isLoading.set(false);
    }
}

export type Usage = {
    error?: string;
    name?: string;
    types?: {
        type: string;
        uses?: {
            name: string;
        }[];
        noUsesMessage?: string;
    }[];
};
